import { useCallback } from 'react'
import {
  useNodeTopPerspectivesLazyQuery,
  NodeTopPerspectivesQuery,
} from '../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import { useExportData } from '../../../../hooks/export-data.hook'

interface UseExportPerspectivesProps {
  nodeId: string
  nodeName: string
  sortBy?: string
  sort?: string
}

type Perspective =
  NodeTopPerspectivesQuery['nodeTopPerspectives']['perspectives'][0]

export function useExportPerspectives({
  nodeId,
  nodeName,
  sortBy,
  sort,
}: UseExportPerspectivesProps) {
  const { t } = useTranslation('nodes')
  const [fetchPerspectives] = useNodeTopPerspectivesLazyQuery()

  const fetchBatch = useCallback(
    async (offset: number, limit: number) => {
      const { data } = await fetchPerspectives({
        variables: {
          input: {
            nodeId,
            limit,
            offset,
            sortBy,
            sort,
          },
        },
      })

      if (!data?.nodeTopPerspectives?.perspectives) {
        return null
      }

      return {
        items: data.nodeTopPerspectives.perspectives,
        total: data.nodeTopPerspectives.total,
      }
    },
    [fetchPerspectives, nodeId, sortBy, sort]
  )

  const getHeaders = () => [
    t('csv-position'),
    t('csv-cluster'),
    t('csv-perspective'),
    t('csv-votes'),
    t('csv-created-date'),
  ]

  const mapRow = useCallback(
    (perspective: Perspective, index: number) => [
      perspective.dbPosition.toString(), // Position
      perspective.cluster?.topic || 'N/A', // Cluster name
      perspective.title, // Perspective name
      perspective.voteCount?.toString() || '0', // Votes
      new Date(perspective.createdAt).toISOString().split('T')[0], // Created date (YYYY-MM-DD)
    ],
    []
  )

  const { exportData, isExporting, progress } = useExportData({
    fetchBatch,
    getHeaders,
    mapRow,
    filename: `${nodeName}-perspectives`,
  })

  return {
    exportPerspectives: exportData,
    isExporting,
    progress,
  }
}
